import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const shouldBeTransparent = isHome && !scrolled;

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          shouldBeTransparent ? 'bg-transparent' : 'bg-white shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={handleLinkClick} className="flex items-center">
              <img
                src={
                  shouldBeTransparent
                    ? 'https://res.cloudinary.com/ditcmszc1/image/upload/v1769763261/imperier-logo-light_bemqas_slewif.png'
                    : 'https://res.cloudinary.com/ditcmszc1/image/upload/v1769764197/imperier-logo-dark_eclumx_y2efho.png'
                }
                alt="Imperier Holdings"
                className="h-12 w-auto transition-all duration-300"
              />
            </Link>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/about-us"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                ABOUT US
              </Link>
              <Link
                to="/portfolio"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                PORTFOLIO
              </Link>
              <Link
                to="/services"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                SERVICES
              </Link>
              <Link
                to="/verticals"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                VERTICALS
              </Link>
              <Link
                to="/media"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                MEDIA
              </Link>
              <Link
                to="/team"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                TEAM
              </Link>
              <Link
                to="/contact-us"
                onClick={handleLinkClick}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-lightBlue ${
                  shouldBeTransparent ? 'text-white' : 'text-black'
                }`}
              >
                CONTACT US
              </Link>
            </div>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex flex-col space-y-1.5 p-2"
              aria-label="Toggle menu"
            >
              <span
                className={`block w-6 h-0.5 transition-colors duration-300 ${
                  shouldBeTransparent ? 'bg-white' : 'bg-black'
                }`}
              ></span>
              <span
                className={`block w-6 h-0.5 transition-colors duration-300 ${
                  shouldBeTransparent ? 'bg-white' : 'bg-black'
                }`}
              ></span>
              <span
                className={`block w-6 h-0.5 transition-colors duration-300 ${
                  shouldBeTransparent ? 'bg-white' : 'bg-black'
                }`}
              ></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-white z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } md:hidden`}
      >
        <div className="flex flex-col h-full">
          {/* Close Button */}
          <div className="flex justify-end p-6">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-black p-2"
              aria-label="Close menu"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Mobile Navigation Links */}
          <div className="flex flex-col items-center justify-center flex-1 space-y-8">
            <Link
              to="/"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              HOME
            </Link>
            <Link
              to="/about-us"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              ABOUT US
            </Link>
            <Link
              to="/portfolio"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              PORTFOLIO
            </Link>
            <Link
              to="/services"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              SERVICES
            </Link>
            <Link
              to="/verticals"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              VERTICALS
            </Link>
            <Link
              to="/media"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              MEDIA
            </Link>
            <Link
              to="/team"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              TEAM
            </Link>
            <Link
              to="/contact-us"
              onClick={handleLinkClick}
              className="text-2xl font-medium text-black hover:text-lightBlue transition-colors duration-200"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;

